/* ==========================================================================
   Loading Screen - Asian American Forward Website
   ========================================================================== */

let loadingComplete = false;

/* ==========================================================================
   Loading Screen Management
   ========================================================================== */

export function initializeLoading() {
  console.log('🔄 Initializing loading screen...');
  
  const loadingScreen = document.getElementById('loading-screen');
  const loadingText = document.querySelector('.loading-text');
  
  if (!loadingScreen) {
    console.warn('Loading screen element not found');
    return;
  }
  
  // Check if this is the user's first visit in this session
  const hasVisitedBefore = sessionStorage.getItem('aaf-visited');
  
  if (hasVisitedBefore) {
    console.log('🔄 Returning visitor - skipping loading screen');
    // Hide loading screen immediately for returning visitors
    loadingScreen.style.display = 'none';
    loadingScreen.setAttribute('aria-hidden', 'true');
    enableMainContent();
    return;
  }
  
  // Mark that user has now visited the site
  sessionStorage.setItem('aaf-visited', 'true');
  console.log('🔄 First visit - showing loading screen');
  
  // Show loading screen immediately, but hide text until fonts are ready
  loadingScreen.style.display = 'flex';
  loadingScreen.setAttribute('aria-hidden', 'false');
  
  if (loadingText) {
    loadingText.style.opacity = '0';
  }
  
  // Wait for fonts to load before showing text
  waitForCriticalFonts().then(() => {
    console.log('✅ Fonts ready - showing loading text');
    if (loadingText) {
      loadingText.style.opacity = '1';
      loadingText.style.transition = 'opacity 0.3s ease';
    }
  }).catch(error => {
    console.warn('Font loading timeout, showing text anyway:', error);
    if (loadingText) {
      loadingText.style.opacity = '1';
      loadingText.style.transition = 'opacity 0.3s ease';
    }
  });
  
  // Wait for page load and minimum display time
  Promise.all([
    waitForPageLoad(),
    waitForMinimumTime(2000) // Minimum 2 seconds to see the animation
  ]).then(() => {
    hideLoadingScreen();
  }).catch(error => {
    console.error('Error during loading:', error);
    // Hide loading screen even if there's an error
    hideLoadingScreen();
  });
}

/* ==========================================================================
   Loading Utilities
   ========================================================================== */

function waitForPageLoad() {
  return new Promise((resolve) => {
    if (document.readyState === 'complete') {
      resolve();
    } else {
      window.addEventListener('load', resolve);
    }
  });
}

function waitForMinimumTime(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function waitForCriticalFonts() {
  return new Promise((resolve, reject) => {
    // Check if document.fonts API is available
    if (!document.fonts) {
      console.warn('document.fonts API not available, proceeding without font check');
      resolve();
      return;
    }
    
    // Set a timeout to prevent waiting too long for fonts
    const timeout = setTimeout(() => {
      reject(new Error('Font loading timeout after 3 seconds'));
    }, 3000);
    
    // Check if Impact font is loaded
    const checkFonts = () => {
      const impactLoaded = document.fonts.check('900 1em Impact');
      
      if (impactLoaded) {
        clearTimeout(timeout);
        console.log('✅ Critical fonts loaded');
        resolve();
      } else {
        // Check again in 50ms
        setTimeout(checkFonts, 50);
      }
    };
    
    // Wait for fonts to be ready, then check
    document.fonts.ready.then(() => {
      checkFonts();
    }).catch(() => {
      // If fonts.ready fails, just check directly
      checkFonts();
    });
  });
}

function hideLoadingScreen() {
  const loadingScreen = document.getElementById('loading-screen');
  if (!loadingScreen || loadingComplete) return;
  
  console.log('✅ Page loaded - hiding loading screen');
  loadingComplete = true;
  
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    // Instant hide for reduced motion
    loadingScreen.style.display = 'none';
    loadingScreen.setAttribute('aria-hidden', 'true');
    enableMainContent();
  } else {
    // Smooth fade out animation
    loadingScreen.classList.add('fade-out');
    
    // Wait for fade animation to complete
    setTimeout(() => {
      loadingScreen.style.display = 'none';
      loadingScreen.setAttribute('aria-hidden', 'true');
      enableMainContent();
    }, 600); // Match CSS animation duration
  }
}

function enableMainContent() {
  // Remove any loading-related body classes
  document.body.classList.remove('loading');
  
  // Ensure main content is visible
  const mainContent = document.getElementById('main-content');
  if (mainContent) {
    mainContent.style.visibility = 'visible';
    mainContent.style.opacity = '1';
  }
  
  // Enable scroll
  document.body.style.overflow = 'auto';
  
  console.log('✅ Main content enabled');
  
  // Dispatch custom event for other modules
  window.dispatchEvent(new CustomEvent('loadingComplete'));
}

/* ==========================================================================
   Loading Progress (Optional Enhancement)
   ========================================================================== */

export function updateLoadingProgress(percentage) {
  const progressBar = document.querySelector('.loading-progress');
  if (progressBar) {
    progressBar.style.width = `${percentage}%`;
  }
}

/* ==========================================================================
   Error Handling
   ========================================================================== */

export function handleLoadingError(error) {
  console.error('Loading error:', error);
  
  // Hide loading screen and show main content even on error
  setTimeout(() => {
    hideLoadingScreen();
  }, 500);
}

/* ==========================================================================
   Browser Support Check
   ========================================================================== */

function checkBrowserSupport() {
  const requiredFeatures = [
    'CSS.supports',
    'requestAnimationFrame',
    'addEventListener'
  ];
  
  const unsupported = requiredFeatures.filter(feature => {
    const parts = feature.split('.');
    let obj = window;
    for (const part of parts) {
      if (!obj || typeof obj[part] === 'undefined') {
        return true;
      }
      obj = obj[part];
    }
    return false;
  });
  
  if (unsupported.length > 0) {
    console.warn('Unsupported browser features:', unsupported);
    // Skip loading animation for unsupported browsers
    hideLoadingScreen();
    return false;
  }
  
  return true;
}

/* ==========================================================================
   Initialization
   ========================================================================== */

// Auto-initialize browser support check
if (typeof window !== 'undefined') {
  checkBrowserSupport();
} 